import { io, Socket } from 'socket.io-client';
import { API_CONFIG } from '../config';
import { ApiService, getAuthToken, getCurrentCompanyId } from '../ApiService';
import type { JournalEntry } from '../../../types/accounting';

/**
 * Regex pour valider un UUID v4
 * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * où x = [0-9a-f] et y = [89ab]
 */
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Valide qu'une chaîne est un UUID v4 valide
 * @param uuid - La chaîne à valider
 * @returns true si c'est un UUID v4 valide
 */
export function isValidUUIDv4(uuid: string): boolean {
  return UUID_V4_REGEX.test(uuid);
}

/**
 * Génère un UUID v4 côté client (RFC 4122 compliant)
 * Utilise crypto.randomUUID() si disponible, sinon crypto.getRandomValues()
 * 
 * @returns UUID v4 string (ex: "550e8400-e29b-41d4-a716-446655440000")
 * @throws Error si la génération échoue
 */
export function generateUUID(): string {
  let uuid: string;
  
  // Méthode 1: Utiliser l'API Web standard (crypto.randomUUID)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    uuid = crypto.randomUUID();
  }
  // Méthode 2: Utiliser crypto.getRandomValues (plus large support)
  else if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    
    // Conformité RFC 4122 version 4:
    // - Octet 6: version 4 (0100xxxx)
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    // - Octet 8: variant 1 (10xxxxxx)  
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    
    // Convertir en chaîne hexadécimale avec tirets
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    uuid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
  }
  // Méthode 3: Fallback Math.random (moins sécurisé, mais valide)
  else {
    uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  
  // Validation finale
  if (!isValidUUIDv4(uuid)) {
    console.error('❌ UUID généré invalide:', uuid);
    throw new Error(`UUID généré invalide: ${uuid}`);
  }
  
  console.log('🔑 UUID v4 généré:', uuid);
  return uuid;
}

/**
 * Types des chunks de streaming selon la documentation API v2.5.0
 * @see chat.md - Section Streaming WebSocket
 * 
 * Types d'événements:
 * - chunk: Fragment de réponse en cours
 * - end: Fin du streaming
 * - error: Erreur durant le streaming
 * - tool_call: Appel d'un outil IA
 * - tool_result: Résultat d'un outil IA
 * - cancelled: Stream annulé par l'utilisateur
 * - heartbeat: Signal de connexion active
 */
export type StreamChunkType = 'chunk' | 'thinking' | 'activity' | 'end' | 'error' | 'tool_call' | 'tool_result' | 'cancelled' | 'heartbeat';

/**
 * Interface StreamingChunkPayload - Conforme à la documentation API v2.4.0
 */
export interface StreamingChunkPayload {
  requestMessageId: string;
  conversationId: string;
  type: StreamChunkType;
  content: string;
  chunkId: number;
  totalChunks?: number;
  journalEntry?: JournalEntry;
  suggestedActions?: Array<{
    type: string;
    label?: string; // Label optionnel ajouté en v2.4.0
    payload: unknown;
  }>;
  processingDetails?: {
    totalChunks?: number;
    contentLength?: number;
    aiModel?: string;
    source?: string;
    processingTime?: number;
    tokensUsed?: number;  // Tokens utilisés par l'IA
  };
  metadata?: Record<string, unknown>;
}

/**
 * Événement de chunk pour le store (avec contenu accumulé)
 */
export interface StreamChunkEvent extends StreamingChunkPayload {
  id: string;
  timestamp: string;
  userId: string;
  companyId: string;
}

/**
 * Callbacks pour le streaming
 */
export interface StreamCallbacks {
  onChunk: (chunk: StreamChunkEvent) => void;
  onEnd: (event: StreamChunkEvent) => void;
  onError: (error: StreamChunkEvent | Error) => void;
  onThinking?: (event: StreamChunkEvent) => void;
  onActivity?: (event: StreamChunkEvent) => void;
  onToolCall?: (event: StreamChunkEvent) => void;
  onToolResult?: (event: StreamChunkEvent) => void;
  onCancelled?: (event: StreamChunkEvent) => void;  // Nouveau callback v2.4.0
  onHeartbeat?: () => void;  // Nouveau callback v2.4.0
  onConnectionChange?: (state: ConnectionState) => void;
}

/**
 * Options de connexion Socket.IO
 */
export interface StreamConnectionOptions {
  timeout?: number;
  reconnectAttempts?: number;
  reconnectDelay?: number;
}

/**
 * Réponse de l'endpoint POST /chat/stream
 */
export interface StreamInitResponse {
  messageId: string;
  conversationId: string;
  userMessageId: string;
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

/**
 * WebSocket Events - Conforme à la documentation API v2.4.0
 */
const WS_EVENTS = {
  // Client → Serveur
  SUBSCRIBE: 'subscribe_conversation',
  UNSUBSCRIBE: 'unsubscribe_conversation',
  CANCEL_STREAM: 'cancel_stream',  // Nouveau v2.4.0 - Annulation de stream
  
  // Serveur → Client
  STREAM_CHUNK: 'adha.stream.chunk',
  STREAM_END: 'adha.stream.end',
  STREAM_ERROR: 'adha.stream.error',
  STREAM_TOOL: 'adha.stream.tool',
  STREAM_CANCELLED: 'adha.stream.cancelled',  // Nouveau v2.4.0
  STREAM_HEARTBEAT: 'adha.stream.heartbeat',  // Nouveau v2.4.0
} as const;

/**
 * Service de streaming pour le chat ADHA
 * Utilise Socket.IO pour la communication en temps réel
 * Conforme à la documentation API v2.3.0
 * 
 * @example
 * ```typescript
 * // Connexion
 * await ChatStreamService.connect({
 *   onChunk: (chunk) => console.log(chunk.content),
 *   onEnd: (event) => console.log('Complet:', event.content),
 *   onError: (error) => console.error(error)
 * });
 * 
 * // Envoi d'un message avec streaming
 * const result = await ChatStreamService.sendStreamMessage({
 *   content: 'Question comptable',
 *   conversationId: 'conv-123'
 * });
 * ```
 */
class ChatStreamServiceClass {
  private socket: Socket | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private callbacks: StreamCallbacks | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectDelay = 2000;
  private pendingMessages: Map<string, { content: string; chunkCount: number }> = new Map();
  private subscribedConversations: Set<string> = new Set();
  private authToken: string | null = null;
  private currentStreamingMessageId: string | null = null;  // Track current streaming message
  private lastHeartbeat: number = 0;  // Track last heartbeat timestamp

  /**
   * URL WebSocket du backend - Configuration Socket.IO
   * 
   * Documentation API v2.3.4 - ARCHITECTURE MULTI-SERVICES:
   * ════════════════════════════════════════════════════════
   * 
   * ⚠️ IMPORTANT: La configuration Socket.IO utilise:
   *   - URL de BASE: ws://host:port (serveur uniquement, SANS path)
   *   - PATH Socket.IO: /accounting/chat (le proxy du service)
   * 
   * Le proxy API Gateway route automatiquement:
   *   /accounting/chat/* → accounting-service/socket.io/* (namespace /chat)
   *   /portfolio/chat/*  → portfolio-service/socket.io/* (futur)
   *   /commerce/chat/*   → commerce-service/socket.io/* (futur)
   * 
   * ════════════════════════════════════════════════════════
   * 
   * CONFIGURATION CORRECTE:
   *   io('ws://localhost:8000', { path: '/accounting/chat', ... })
   * 
   * ❌ CONFIGURATION INCORRECTE:
   *   io('ws://localhost:8000/accounting/chat', { path: '/socket.io', ... })
   */
  private getWebSocketBaseUrl(): string {
    const apiUrl = API_CONFIG.baseUrl;
    const url = new URL(apiUrl);
    const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    
    // URL de BASE uniquement: ws://host:port (SANS path)
    const baseUrl = `${protocol}//${url.host}`;
    console.log(`🔌 WebSocket Base URL: ${baseUrl}`);
    return baseUrl;
  }

  /**
   * Path Socket.IO pour le service accounting
   * Le proxy API Gateway redirige ce path vers le backend
   */
  private getSocketIOPath(): string {
    const apiUrl = API_CONFIG.baseUrl;
    const url = new URL(apiUrl);
    
    // Extraire le préfixe de service (ex: /accounting depuis /accounting/api/v1)
    const pathParts = url.pathname.split('/').filter(Boolean);
    const servicePrefix = pathParts.length > 0 ? `/${pathParts[0]}` : '';
    
    // Path Socket.IO: /accounting/chat (le proxy du service)
    const socketPath = `${servicePrefix}/chat`;
    console.log(`🔌 Socket.IO Path: ${socketPath}`);
    return socketPath;
  }

  /**
   * Vérifie si Socket.IO est supporté
   */
  isSupported(): boolean {
    return typeof window !== 'undefined' && typeof io !== 'undefined';
  }

  /**
   * État de la connexion
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Définit le token d'authentification
   */
  setAuthToken(token: string): void {
    this.authToken = token;
    console.log('🔐 Token défini pour WebSocket streaming');
  }

  /**
   * Connecte au serveur Socket.IO avec les callbacks
   * Récupère automatiquement le token via le provider si non défini manuellement
   */
  async connect(callbacks: StreamCallbacks, options?: StreamConnectionOptions): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('Socket.IO non supporté par ce navigateur');
    }

    // Si déjà connecté, mettre à jour les callbacks
    if (this.connectionState === 'connected' && this.socket?.connected) {
      this.callbacks = callbacks;
      return;
    }

    // Récupérer le token automatiquement si non défini
    if (!this.authToken) {
      console.log('🔐 Récupération automatique du token via ApiService...');
      const token = await getAuthToken();
      if (token) {
        this.authToken = token;
        console.log('🔐 Token récupéré automatiquement');
      } else {
        console.warn('⚠️ Aucun token disponible pour WebSocket');
      }
    }

    this.callbacks = callbacks;
    this.maxReconnectAttempts = options?.reconnectAttempts ?? 3;
    this.reconnectDelay = options?.reconnectDelay ?? 2000;
    const timeout = options?.timeout ?? 30000;

    return new Promise((resolve, reject) => {
      try {
        this.setConnectionState('connecting');
        const wsBaseUrl = this.getWebSocketBaseUrl();
        const socketPath = this.getSocketIOPath();
        
        console.log('🔌 Connexion Socket.IO:', { baseUrl: wsBaseUrl, path: socketPath });

        /**
         * Configuration Socket.IO selon documentation chat.md v2.3.4
         * 
         * ✅ CONFIGURATION CORRECTE (selon chat.md):
         *   - URL: ws://localhost:8000 (serveur de BASE uniquement)
         *   - path: '/accounting/chat' (le proxy du service)
         *   - transports: ['websocket'] (WebSocket uniquement)
         * 
         * ❌ CONFIGURATION INCORRECTE:
         *   - URL: ws://localhost:8000/accounting/chat + path: '/socket.io'
         * 
         * Le proxy API Gateway route:
         *   /accounting/chat/* → accounting-service/socket.io/* (namespace /chat)
         */
        // Construire l'auth Socket.IO avec companyId pour les auditeurs
        const socketAuth: Record<string, string> = {};
        if (this.authToken) {
          socketAuth.token = this.authToken;
        }
        const companyId = getCurrentCompanyId();
        if (companyId) {
          socketAuth.companyId = companyId;
        }

        this.socket = io(wsBaseUrl, {
          path: socketPath,                       // ⚠️ CRITIQUE: /accounting/chat (proxy)
          transports: ['websocket'],              // WebSocket uniquement (selon chat.md)
          timeout,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
          auth: Object.keys(socketAuth).length > 0 ? socketAuth : undefined,
          forceNew: false,                        // Réutiliser connexion existante si possible
        });

        console.log('🔌 Socket.IO options:', {
          url: wsBaseUrl,
          path: socketPath,
          transports: ['websocket'],
          hasAuth: !!this.authToken
        });

        const connectionTimeout = setTimeout(() => {
          if (this.connectionState === 'connecting') {
            console.error('⏰ Timeout de connexion Socket.IO après', timeout, 'ms');
            this.socket?.disconnect();
            reject(new Error('Timeout de connexion Socket.IO'));
          }
        }, timeout);

        this.socket.on('connect', () => {
          clearTimeout(connectionTimeout);
          this.setConnectionState('connected');
          this.reconnectAttempts = 0;
          console.log('✅ Socket.IO connecté avec succès!', {
            socketId: this.socket?.id,
            connected: this.socket?.connected,
            namespace: (this.socket as any)?.nsp || 'N/A'
          });
          
          // Réabonner aux conversations précédentes
          this.resubscribeConversations();
          resolve();
        });

        this.socket.on('disconnect', (reason) => {
          console.log('🔌 Socket.IO déconnecté:', reason);
          
          if (reason === 'io server disconnect') {
            // Déconnexion forcée par le serveur
            this.setConnectionState('disconnected');
          } else {
            // Tentative de reconnexion automatique par Socket.IO
            this.setConnectionState('reconnecting');
          }
        });

        this.socket.on('connect_error', (error) => {
          clearTimeout(connectionTimeout);
          console.error('❌ Erreur connexion Socket.IO:', {
            message: error.message,
            type: (error as any).type,
            description: (error as any).description
          });
          this.setConnectionState('error');
          
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            reject(new Error(`Impossible de se connecter: ${error.message}`));
          }
        });

        this.socket.io.on('reconnect', (attempt) => {
          console.log(`🔄 Reconnecté après ${attempt} tentatives`);
          this.setConnectionState('connected');
          this.resubscribeConversations();
        });

        this.socket.io.on('reconnect_attempt', (attempt) => {
          this.reconnectAttempts = attempt;
          console.log(`🔄 Tentative de reconnexion ${attempt}/${this.maxReconnectAttempts}...`);
        });

        this.socket.io.on('reconnect_failed', () => {
          console.error('❌ Reconnexion échouée');
          this.setConnectionState('error');
        });

        // Écouter les événements de streaming
        this.setupStreamListeners();

      } catch (error) {
        this.setConnectionState('error');
        reject(error);
      }
    });
  }

  /**
   * Met à jour l'état de connexion et notifie le callback
   */
  private setConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.callbacks?.onConnectionChange?.(state);
  }

  /**
   * Configure les listeners pour les événements de streaming
   */
  private setupStreamListeners(): void {
    if (!this.socket) {
      console.error('❌ Impossible de configurer les listeners: socket null');
      return;
    }

    console.log('🎧 Configuration des listeners de streaming...');

    // Événement chunk - Nouveau chunk de texte
    this.socket.on(WS_EVENTS.STREAM_CHUNK, (payload: StreamingChunkPayload) => {
      console.log('📨 Événement STREAM_CHUNK reçu');
      this.handleChunk(payload);
    });

    // Événement end - Fin du stream
    this.socket.on(WS_EVENTS.STREAM_END, (payload: StreamingChunkPayload) => {
      console.log('✅ Événement STREAM_END reçu');
      this.handleEnd(payload);
    });

    // Événement error - Erreur de streaming
    this.socket.on(WS_EVENTS.STREAM_ERROR, (payload: StreamingChunkPayload) => {
      console.log('❌ Événement STREAM_ERROR reçu');
      this.handleError(payload);
    });

    // Événement tool - Appel/résultat d'outil IA
    this.socket.on(WS_EVENTS.STREAM_TOOL, (payload: StreamingChunkPayload) => {
      console.log('🔧 Événement STREAM_TOOL reçu');
      this.handleToolEvent(payload);
    });

    // Réponse de souscription
    this.socket.on(WS_EVENTS.SUBSCRIBE, (response: { success: boolean; conversationId: string; error?: string }) => {
      if (response.success) {
        console.log('✅ Abonné à la conversation:', response.conversationId);
        this.subscribedConversations.add(response.conversationId);
      } else {
        console.error('❌ Échec souscription:', response.error);
      }
    });

    // Événement cancelled - Stream annulé v2.4.0
    this.socket.on(WS_EVENTS.STREAM_CANCELLED, (payload: StreamingChunkPayload) => {
      console.log('🛑 Événement STREAM_CANCELLED reçu');
      this.handleCancelled(payload);
    });

    // Événement heartbeat - Signal de connexion active v2.4.0
    this.socket.on(WS_EVENTS.STREAM_HEARTBEAT, () => {
      this.lastHeartbeat = Date.now();
      console.log('💓 Heartbeat reçu');
      this.callbacks?.onHeartbeat?.();
    });

    // Événement générique pour debug
    this.socket.onAny((eventName, ...args) => {
      if (!eventName.startsWith('adha.stream')) {
        console.log(`📡 Événement Socket.IO reçu: ${eventName}`, args);
      }
    });

    console.log('🎧 Listeners configurés pour:', Object.values(WS_EVENTS));
  }

  /**
   * Gère un chunk de contenu
   */
  private handleChunk(payload: StreamingChunkPayload): void {
    const { requestMessageId, content, chunkId, conversationId } = payload;
    
    // Thinking chunks: forward directly without accumulation
    if (payload.type === 'thinking') {
      console.log(`🧠 THINKING chunk ${chunkId} for ${conversationId}`);
      const event = this.createStreamEvent(payload, content);
      this.callbacks?.onThinking?.(event);
      return;
    }
    
    // Activity events: forward directly without accumulation (file lookups, KB searches)
    if (payload.type === 'activity') {
      console.log(`📂 ACTIVITY event for ${conversationId}: ${content?.substring(0, 80)}`);
      const event = this.createStreamEvent(payload, content);
      this.callbacks?.onActivity?.(event);
      return;
    }
    
    console.log(`📨 CHUNK ${chunkId} reçu pour conversation ${conversationId}:`, {
      messageId: requestMessageId,
      contentLength: content?.length || 0,
      contentPreview: content?.substring(0, 50) || ''
    });

    // Accumuler le contenu
    const pending = this.pendingMessages.get(requestMessageId) || { content: '', chunkCount: 0 };
    pending.content += content;
    pending.chunkCount++;
    this.pendingMessages.set(requestMessageId, pending);

    // Créer l'événement avec le contenu accumulé
    const event = this.createStreamEvent(payload, pending.content);
    
    this.callbacks?.onChunk?.(event);
  }

  /**
   * Gère la fin du stream
   */
  private handleEnd(payload: StreamingChunkPayload): void {
    const { requestMessageId, conversationId, processingDetails } = payload;
    
    console.log(`✅ STREAM END pour conversation ${conversationId}:`, {
      messageId: requestMessageId,
      totalChunks: processingDetails?.totalChunks,
      contentLength: processingDetails?.contentLength,
      processingTime: processingDetails?.processingTime
    });

    // Récupérer le contenu accumulé
    const pending = this.pendingMessages.get(requestMessageId);
    const finalContent = payload.content || pending?.content || '';
    
    console.log('📊 Contenu final:', {
      fromPayload: !!payload.content,
      fromPending: !!pending?.content,
      finalLength: finalContent.length,
      chunksReceived: pending?.chunkCount || 0
    });

    // Nettoyer
    this.pendingMessages.delete(requestMessageId);

    const event = this.createStreamEvent(payload, finalContent);
    
    this.callbacks?.onEnd?.(event);
  }

  /**
   * Gère une erreur de streaming
   */
  private handleError(payload: StreamingChunkPayload): void {
    const { requestMessageId, conversationId, content } = payload;
    
    console.error(`❌ STREAM ERROR pour conversation ${conversationId}:`, {
      messageId: requestMessageId,
      error: content
    });

    // Nettoyer
    this.pendingMessages.delete(requestMessageId);
    this.currentStreamingMessageId = null;

    const event = this.createStreamEvent(payload, payload.content);
    
    this.callbacks?.onError?.(event);
  }

  /**
   * Gère l'annulation d'un stream (v2.4.0)
   */
  private handleCancelled(payload: StreamingChunkPayload): void {
    const { requestMessageId, conversationId, content } = payload;
    
    console.log(`🛑 STREAM CANCELLED pour conversation ${conversationId}:`, {
      messageId: requestMessageId,
      reason: content
    });

    // Nettoyer
    this.pendingMessages.delete(requestMessageId);
    this.currentStreamingMessageId = null;

    const event = this.createStreamEvent(payload, content || 'Stream annulé');
    
    this.callbacks?.onCancelled?.(event);
  }

  /**
   * Gère un événement d'outil (tool_call ou tool_result)
   */
  private handleToolEvent(payload: StreamingChunkPayload): void {
    const event = this.createStreamEvent(payload, payload.content);
    
    if (payload.type === 'tool_call') {
      this.callbacks?.onToolCall?.(event);
    } else if (payload.type === 'tool_result') {
      this.callbacks?.onToolResult?.(event);
    }
  }

  /**
   * Crée un StreamChunkEvent à partir d'un payload
   */
  private createStreamEvent(payload: StreamingChunkPayload, content: string): StreamChunkEvent {
    return {
      ...payload,
      content,
      id: payload.requestMessageId,
      timestamp: new Date().toISOString(),
      userId: '',
      companyId: '',
    };
  }

  /**
   * S'abonne aux mises à jour d'une conversation
   */
  subscribeToConversation(conversationId: string): void {
    if (!this.socket?.connected) {
      console.warn('Socket non connecté, impossible de s\'abonner');
      return;
    }

    this.socket.emit(WS_EVENTS.SUBSCRIBE, { conversationId });
  }

  /**
   * Se désabonne d'une conversation
   */
  unsubscribeFromConversation(conversationId: string): void {
    if (!this.socket?.connected) return;

    this.socket.emit(WS_EVENTS.UNSUBSCRIBE, { conversationId });
    this.subscribedConversations.delete(conversationId);
  }

  /**
   * Réabonne aux conversations après reconnexion
   */
  private resubscribeConversations(): void {
    this.subscribedConversations.forEach(convId => {
      this.socket?.emit(WS_EVENTS.SUBSCRIBE, { conversationId: convId });
    });
  }

  /**
   * Annule le stream en cours (v2.4.0)
   * Envoie l'événement cancel_stream au serveur via WebSocket
   * 
   * @param messageId - ID du message dont le stream doit être annulé (optionnel)
   * @returns boolean - true si l'annulation a été envoyée
   */
  cancelStream(messageId?: string): boolean {
    const targetMessageId = messageId || this.currentStreamingMessageId;
    
    if (!this.socket?.connected) {
      console.warn('⚠️ Socket non connecté, impossible d\'annuler le stream');
      return false;
    }

    if (!targetMessageId) {
      console.warn('⚠️ Aucun stream en cours à annuler');
      return false;
    }

    console.log('🛑 Envoi cancel_stream pour message:', targetMessageId);
    
    this.socket.emit(WS_EVENTS.CANCEL_STREAM, { 
      messageId: targetMessageId 
    });

    // Nettoyer localement
    this.pendingMessages.delete(targetMessageId);
    this.currentStreamingMessageId = null;

    return true;
  }

  /**
   * Vérifie si un stream est en cours
   */
  isCurrentlyStreaming(): boolean {
    return this.currentStreamingMessageId !== null;
  }

  /**
   * Retourne l'ID du message en streaming
   */
  getCurrentStreamingMessageId(): string | null {
    return this.currentStreamingMessageId;
  }

  /**
   * Retourne le timestamp du dernier heartbeat
   */
  getLastHeartbeat(): number {
    return this.lastHeartbeat;
  }

  /**
   * Envoie un message avec streaming via l'endpoint REST
   * Retourne immédiatement, les réponses arrivent via WebSocket
   * 
   * 🆕 v2.5.0: Client-Generated conversationId
   * ═══════════════════════════════════════════════════════════════════
   * Pour garantir un streaming temps réel dès le PREMIER message,
   * le frontend génère maintenant le conversationId côté client.
   * 
   * Workflow v2.5.0:
   * 1. Générer le conversationId côté client si nouvelle conversation
   * 2. S'abonner à la room WebSocket AVANT l'appel HTTP
   * 3. Appeler POST /chat/stream avec le conversationId (toujours fourni)
   * 4. Le backend utilise l'ID fourni (pas de race condition!)
   * 5. Les chunks arrivent via WebSocket dès le début
   * ═══════════════════════════════════════════════════════════════════
   * 
   * @see POST /chat/stream
   * @see API DOCUMENTATION/chat.md - Section "Streaming v2.5.0"
   */
  async sendStreamMessage(params: {
    conversationId?: string;
    content: string;
    attachment?: { name: string; type: string; content: string };
    writeMode?: boolean;
    thinkingMode?: boolean;
    context?: string[];
    companyId?: string;
  }): Promise<StreamInitResponse> {
    
    // Vérifier que le socket est connecté
    if (!this.socket?.connected) {
      console.error('❌ WebSocket non connecté! Impossible d\'utiliser le streaming.');
      throw new Error('WebSocket non connecté. Veuillez réessayer ou désactiver le streaming.');
    }

    // 🆕 ÉTAPE 1 (v2.5.0): Toujours avoir un conversationId AVANT d'envoyer
    // Si nouvelle conversation, générer l'ID côté client
    const conversationId = params.conversationId || generateUUID();
    const isNewConversation = !params.conversationId;

    console.log('🚀 sendStreamMessage v2.5.0 démarré', {
      conversationId,
      isNewConversation,
      isClientGenerated: isNewConversation,
      socketConnected: this.socket?.connected,
      socketId: this.socket?.id
    });

    // 🆕 ÉTAPE 2 (v2.5.0): S'abonner AVANT l'appel HTTP - fonctionne pour TOUTES les conversations
    // Ceci élimine la race condition pour les nouvelles conversations
    console.log('📡 ÉTAPE 2 (v2.5.0): Pré-abonnement à la conversation:', conversationId);
    this.subscribeToConversation(conversationId);
    
    // Attendre la confirmation de souscription (avec timeout de sécurité)
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        console.log('⚠️ Timeout souscription, continuation...');
        resolve();
      }, 500);
      
      const handleSubscribeResponse = (response: { success: boolean; conversationId: string; error?: string }) => {
        if (response.conversationId === conversationId) {
          clearTimeout(timeout);
          if (response.success) {
            console.log('✅ Pré-abonnement confirmé pour:', conversationId);
          } else {
            console.warn('⚠️ Échec pré-abonnement:', response.error);
          }
          this.socket?.off(WS_EVENTS.SUBSCRIBE, handleSubscribeResponse);
          resolve();
        }
      };
      
      this.socket?.on(WS_EVENTS.SUBSCRIBE, handleSubscribeResponse);
    });

    const request = {
      conversationId, // ✅ Toujours fourni (client-generated ou existant)
      message: {
        content: params.content,
        attachment: params.attachment
      },
      writeMode: params.writeMode ?? false,
      thinkingMode: params.thinkingMode ?? false,
      context: params.context ?? [],
      ...(params.companyId ? { companyId: params.companyId } : {}),
      // Attachment fields at top level for Django backend compatibility
      ...(params.attachment ? {
        attachment_url: params.attachment.content,
        attachment_name: params.attachment.name,
        attachment_type: params.attachment.type
      } : {})
    };

    // ÉTAPE 3: Appeler POST /chat/stream
    console.log('📤 ÉTAPE 3 (v2.5.0): Appel POST /chat/stream...', { 
      endpoint: '/chat/stream', 
      writeMode: request.writeMode,
      conversationId: request.conversationId 
    });
    const startTime = Date.now();
    
    const response = await ApiService.post<{
      messageId: string;
      conversationId: string;
      userMessageId: string;
    }>('/chat/stream', request);

    console.log('📥 Réponse POST /chat/stream reçue en', Date.now() - startTime, 'ms', response);

    if (!response.success || !response.data) {
      console.error('❌ Erreur POST /chat/stream:', response.error);
      throw new Error(response.error || 'Erreur lors de l\'initiation du streaming');
    }

    // Vérifier que le backend a utilisé notre conversationId
    if (response.data.conversationId !== conversationId) {
      console.warn('⚠️ Backend a généré un ID différent:', {
        sent: conversationId,
        received: response.data.conversationId
      });
      // S'abonner aussi au nouveau ID par sécurité
      this.subscribeToConversation(response.data.conversationId);
    }

    // 🎉 ÉTAPE 4: Les événements adha.stream.* arrivent via les callbacks configurés
    // Pas de souscription tardive nécessaire - c'est déjà fait!
    console.log('✅ ÉTAPE 4 (v2.5.0): Streaming initié avec succès - tous les chunks seront reçus!', {
      messageId: response.data.messageId,
      conversationId: response.data.conversationId,
      userMessageId: response.data.userMessageId,
      isNewConversation,
      preSubscribed: true
    });

    // Tracker le message en streaming pour permettre l'annulation
    this.currentStreamingMessageId = response.data.messageId;

    return response.data;
  }

  /**
   * Déconnecte Socket.IO
   */
  disconnect(): void {
    this.pendingMessages.clear();
    this.subscribedConversations.clear();
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.setConnectionState('disconnected');
    this.callbacks = null;
    console.log('🔌 Socket.IO déconnecté');
  }

  /**
   * Vérifie si connecté
   */
  isConnected(): boolean {
    return this.connectionState === 'connected' && this.socket?.connected === true;
  }
}

// Singleton export
export const ChatStreamService = new ChatStreamServiceClass();
